import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Broom, MagnifyingGlass, CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";

const FIELD_LABELS = {
  emi_due_date: "Payment Due Date",
  disbursal_date: "Disbursal Date",
  last_payment_date: "Last Payment Date",
  next_follow_up_date: "Next Follow-up Date",
};

const StatCard = ({ label, value, accent, testId }) => (
  <div className="border border-border bg-card p-4" data-testid={testId}>
    <div className="label-caps text-muted-foreground">{label}</div>
    <div className={`mt-1 text-2xl font-black font-heading ${accent || ""}`}>{value}</div>
  </div>
);

export default function DataHygiene() {
  const [scope, setScope] = useState("");
  const [report, setReport] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);

  const call = (dryRun) => {
    const qs = new URLSearchParams({ dry_run: String(dryRun) });
    if (scope.trim()) qs.set("tenant_id", scope.trim());
    return api.post(`/admin/backfill-iso-dates?${qs.toString()}`);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await call(true);
      setReport(data);
      toast.success(`Preview complete — ${data.accounts_to_update} account(s) need normalization`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const applyFix = async () => {
    setApplying(true);
    try {
      const { data } = await call(false);
      toast.success(`Applied — ${data.applied} account(s) normalized to ISO dates`);
      const { data: fresh } = await call(true);
      setReport(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const toUpdate = report?.accounts_to_update ?? 0;
  const totalUnparseable = report
    ? Object.values(report.per_field).reduce((s, f) => s + (f.unparseable || 0), 0)
    : 0;

  return (
    <div data-testid="data-hygiene-page">
      <PageHeader
        title="Data Hygiene"
        subtitle="Normalize legacy date fields to a clean ISO format (YYYY-MM-DD)."
      />

      <div className="border border-border bg-secondary/40 p-4 mb-6 text-sm text-muted-foreground" data-testid="data-hygiene-info">
        Older imports may have stored dates in mixed formats (e.g. <span className="mono">15/05/2025</span>).
        This tool rewrites them to clean ISO (<span className="mono">2025-05-15</span>) — the actual date is
        unchanged, so <strong>DPD and buckets are not affected</strong>. Unparseable values are left untouched
        and flagged for manual review. Always run a preview first.
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="w-64">
          <label className="label-caps text-muted-foreground block mb-1">Tenant scope (optional)</label>
          <Input
            placeholder="Leave blank = all tenants"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="rounded-sm"
            data-testid="data-hygiene-tenant-input"
          />
        </div>
        <Button
          onClick={runPreview}
          disabled={previewing || applying}
          className="rounded-sm gap-1.5"
          data-testid="data-hygiene-preview-btn"
        >
          <MagnifyingGlass size={16} /> {previewing ? "Scanning…" : "Run Preview"}
        </Button>

        {report && toUpdate > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="default"
                disabled={applying || previewing}
                className="rounded-sm gap-1.5 bg-[#0B3D91] hover:bg-[#0a347d]"
                data-testid="data-hygiene-apply-btn"
              >
                <Broom size={16} /> {applying ? "Applying…" : `Apply to ${toUpdate} account(s)`}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="data-hygiene-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Normalize {toUpdate} account(s)?</AlertDialogTitle>
                <AlertDialogDescription>
                  This rewrites date fields to ISO format for {report.scope === "ALL" ? "all tenants" : `tenant ${report.scope}`}.
                  The date values themselves do not change, so DPD is unaffected. This action is safe but not automatically reversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="data-hygiene-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={applyFix} data-testid="data-hygiene-confirm-apply">
                  Yes, normalize
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard label="Accounts scanned" value={report.scanned} testId="data-hygiene-stat-scanned" />
            <StatCard label="To normalize" value={toUpdate} accent={toUpdate ? "text-amber-700" : "text-green-700"} testId="data-hygiene-stat-toupdate" />
            <StatCard label="Unparseable (manual)" value={totalUnparseable} accent={totalUnparseable ? "text-red-700" : ""} testId="data-hygiene-stat-unparseable" />
            <StatCard label="Applied this run" value={report.applied} accent="text-green-700" testId="data-hygiene-stat-applied" />
          </div>

          {toUpdate === 0 && totalUnparseable === 0 && (
            <div className="flex items-center gap-2 text-green-700 font-semibold mb-6" data-testid="data-hygiene-clean">
              <CheckCircle size={18} weight="fill" /> All dates are already in clean ISO format. Nothing to do.
            </div>
          )}

          <div className="space-y-5">
            {Object.entries(report.per_field).map(([key, f]) => (
              <div key={key} className="border border-border bg-card" data-testid={`data-hygiene-field-${key}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/40">
                  <div className="font-semibold">{FIELD_LABELS[key] || key} <span className="mono text-xs text-muted-foreground">({key})</span></div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="font-semibold text-amber-700">{f.normalize} to fix</span>
                    {f.unparseable > 0 && (
                      <span className="inline-flex items-center gap-1 text-red-700 font-semibold">
                        <Warning size={13} weight="fill" /> {f.unparseable} unparseable
                      </span>
                    )}
                  </div>
                </div>
                {f.samples.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left label-caps text-muted-foreground border-b border-border/60">
                        <th className="px-4 py-2">Reference</th>
                        <th className="px-4 py-2">Before</th>
                        <th className="px-4 py-2"></th>
                        <th className="px-4 py-2">After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {f.samples.map((s, i) => (
                        <tr key={i} className="border-b border-border/40" data-testid={`data-hygiene-sample-${key}-${i}`}>
                          <td className="px-4 py-2 font-semibold">{s.ref}</td>
                          <td className="px-4 py-2 mono text-red-700">{s.before}</td>
                          <td className="px-4 py-2 text-muted-foreground"><ArrowRight size={14} /></td>
                          <td className="px-4 py-2 mono text-green-700">{s.after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="px-4 py-3 text-sm text-muted-foreground" data-testid={`data-hygiene-nofix-${key}`}>
                    No changes needed{f.unparseable > 0 ? " (only unparseable values remain — fix these manually)." : "."}
                  </div>
                )}
                {f.samples.length > 0 && f.normalize > f.samples.length && (
                  <div className="px-4 py-2 text-xs text-muted-foreground">
                    …and {f.normalize - f.samples.length} more.
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
